import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { History, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/ui-bits";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function AuditLogPanel() {
  const [tab, setTab] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async (which = tab) => {
    setLoading(true);
    try {
      const qs = which === "stock_reset" ? "?action=stock_reset,stock_take_apply&limit=300" : "?limit=300";
      const { data } = await api.get(`/admin/audit/log${qs}`);
      setRows(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const fmtChanges = (c) => {
    if (!c || Object.keys(c).length === 0) return "—";
    return Object.entries(c)
      .map(([k, v]) => {
        if (v && typeof v === "object" && "from" in v && "to" in v) {
          return `${k}: ${JSON.stringify(v.from)} → ${JSON.stringify(v.to)}`;
        }
        if (k === "top_materials" && Array.isArray(v)) {
          if (v.length === 0) return `${k}: —`;
          return `${k}: ${v
            .map((m) => `${m.name || "?"} ${m.wiped}${m.unit || ""}`)
            .join(", ")}`;
        }
        return `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`;
      })
      .join(" · ");
  };

  const actionStyle = (action) => {
    if (action === "stock_reset" || action?.startsWith("stock_take") || action === "intake_bulk_archive") return "bg-rose-100 text-rose-800";
    if (action?.startsWith("user_")) return "bg-blue-100 text-blue-800";
    if (action?.startsWith("role_")) return "bg-indigo-100 text-indigo-800";
    if (action?.includes("login") || action?.includes("otp"))
      return "bg-amber-100 text-amber-800";
    return "bg-stone-100 text-stone-700";
  };

  return (
    <Panel
      title={`Audit Log (${rows.length})`}
      action={
        <Button
          size="sm"
          variant="outline"
          onClick={() => load(tab)}
          data-testid="audit-refresh"
        >
          <History className="h-4 w-4 mr-1.5" /> Refresh
        </Button>
      }
    >
      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="all" data-testid="audit-tab-all">
            All events
          </TabsTrigger>
          <TabsTrigger
            value="stock_reset"
            data-testid="audit-tab-stock-reset"
            className="gap-1.5"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Stock resets
          </TabsTrigger>
        </TabsList>

        <TabsContent value={tab}>
          {tab === "stock_reset" && (
            <div className="text-xs text-stone-500 mb-3">
              Admin-only stock-take resets. Each row shows who ran the reset,
              when, how many intakes were zeroed and the top wiped materials —
              retain for BRCGS / SALSA compliance evidence.
            </div>
          )}

          {loading ? (
            <div className="text-sm text-stone-500 py-6">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-stone-500 py-6" data-testid="audit-empty">
              {tab === "stock_reset"
                ? "No stock resets recorded yet."
                : "No audit entries yet."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="audit-table">
                <thead className="text-left text-xs uppercase tracking-wider text-stone-500">
                  <tr>
                    <th className="py-3">When</th>
                    <th>Who</th>
                    <th>Action</th>
                    <th>Target</th>
                    {tab === "stock_reset" ? (
                      <>
                        <th className="text-right">Rows wiped</th>
                        <th>Top materials</th>
                      </>
                    ) : (
                      <th>Changes</th>
                    )}
                    <th>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.id}
                      className="border-t border-border align-top"
                      data-testid={`audit-row-${r.id}`}
                    >
                      <td className="py-2.5 text-stone-600 text-xs font-mono whitespace-nowrap">
                        {r.timestamp?.slice(0, 19).replace("T", " ")}
                      </td>
                      <td className="py-2.5 text-sm">
                        <div className="font-medium">{r.actor_name}</div>
                        <div className="text-xs text-stone-500">{r.actor_role}</div>
                      </td>
                      <td className="py-2.5">
                        <span
                          className={`pill-stage font-mono text-xs ${actionStyle(
                            r.action
                          )}`}
                        >
                          {r.action}
                        </span>
                      </td>
                      <td className="py-2.5 text-sm">{r.target_summary || "—"}</td>
                      {tab === "stock_reset" ? (
                        <StockResetCells changes={r.changes} />
                      ) : (
                        <td
                          className="py-2.5 text-xs text-stone-600 max-w-md truncate"
                          title={fmtChanges(r.changes)}
                        >
                          {fmtChanges(r.changes)}
                        </td>
                      )}
                      <td className="py-2.5 text-xs text-stone-500 font-mono">
                        {r.ip || ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </Panel>
  );
}

function StockResetCells({ changes }) {
  const rowsUpdated = changes?.rows_updated ?? 0;
  const materialsWiped = changes?.materials_wiped ?? 0;
  const tops = Array.isArray(changes?.top_materials) ? changes.top_materials : [];
  return (
    <>
      <td className="py-2.5 text-right">
        <div className="font-semibold tnum">{rowsUpdated}</div>
        <div className="text-[10px] text-stone-500">{materialsWiped} materials</div>
      </td>
      <td className="py-2.5 text-xs text-stone-600 max-w-md">
        {tops.length === 0 ? (
          <span className="text-stone-400">—</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {tops.slice(0, 8).map((m, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 rounded-full bg-rose-50 text-rose-800 border border-rose-200 px-2 py-0.5 text-[11px]"
              >
                {m.name}
                <span className="font-mono text-rose-700">
                  {m.wiped}
                  {m.unit}
                </span>
              </span>
            ))}
            {tops.length > 8 && (
              <span className="text-[11px] text-stone-500">
                +{tops.length - 8} more
              </span>
            )}
          </div>
        )}
      </td>
    </>
  );
}
